import React, { useContext, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';
import { BiData, PackagePickerClickBiEvent } from '@wix/da-bi/pkg/events';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import UserSymbol from '@wix/da-shared-react/pkg/User/_partials/UserSymbol';
import { URLS } from '@wix/da-url';
import { PapiCorePackage, PapiSubscription } from '@wix/da-papi-types';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import BillingCyclePopup from '../Popup/BillingCyclePopup';
import { addSubproductIdToURL } from '../../../helpers/url';
import { CoreTypes } from '../types';
import { type PackagePickerStoreSlice } from '../../../redux/packagePicker/types';
import {
  getPriceBeforeDiscountForPackagePicker,
  getPriceForPackagePicker,
  getRecommendedPackageType,
} from '../utils';
import { packagesData } from '../constants';
import useOpenCPPopupOnIdParam from '../hooks/useOpenCPPopupOnIdParam';

import s from './CorePrice.scss';

export interface Props {
  coreType: CoreTypes;
  packages: PapiCorePackage[];
  pickerMode: PackagePickerStoreSlice['pickerMode'];
  packageConfig?: PackagePickerStoreSlice['packageConfig'];
  alreadyHasSubscription: boolean;
  currentCoreSubscription?: PapiSubscription;
  highlighted?: boolean;
  sale: PackagePickerStoreSlice['sale'];
}

const GET_NOTIFIED_URL =
  'https://www.deviantartsupport.com/en/article/changing-your-core-membership-package';

const CorePrice: React.FC<Props> = ({
  coreType,
  packages,
  pickerMode,
  packageConfig,
  alreadyHasSubscription,
  currentCoreSubscription,
  sale,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const [selectedPackage, setSelectedPackage] = useState<number[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openBilling = () => setIsPopupOpen(true);

  const monthlyPackage = packages.filter(
    p => p.coreType === coreType && p.interval === 'month'
  )[0];
  const yearlyPackage = packages.filter(
    p => p.coreType === coreType && p.interval === 'year'
  )[0];

  const isGift = pickerMode === 'gift';
  const isCurrent = currentCoreSubscription?.corePackage?.coreType === coreType;
  const isPackageDisabled =
    packageConfig?.[camelCase(coreType)]?.enabled === false;

  const isMonthlyPackageDisabled =
    monthlyPackage.status === 'disabled' ||
    (monthlyPackage.status === 'manage' && isGift);

  const isDisabled = isPackageDisabled || isMonthlyPackageDisabled;

  // Discount might apply to monthly or yearly packages only, or both.
  const hasDiscount = Boolean(
    (yearlyPackage.discount || monthlyPackage.discount) && sale?.ppShowDiscount
  );

  useOpenCPPopupOnIdParam({
    monthlyPackage,
    yearlyPackage,
    setSelectedPackage,
    setIsPopupOpen,
  });

  const handleSelect = selected => {
    // TODO: Talk to BE team and define whether we need a "status" field at all
    if (monthlyPackage.status === 'manage') {
      window.location.assign(URLS.SETTINGS_REBILLING);
      return;
    }
    if (monthlyPackage.status === 'notify') {
      window.location.assign(GET_NOTIFIED_URL);
      return;
    }

    setSelectedPackage(selected);
    openBilling();

    addSubproductIdToURL(selected[0]);
  };

  let biButtonType = 'gift';
  if (pickerMode === 'buy') {
    biButtonType =
      monthlyPackage.status === 'manage'
        ? 'manage'
        : monthlyPackage.status === 'notify'
        ? 'notify'
        : 'select';
  }

  const biCoreType = coreType === CoreTypes.CORE_ACCESS ? 'core' : coreType;

  const isRecommended = getRecommendedPackageType(packageConfig) === coreType;

  const showCTA =
    isGift || !currentCoreSubscription || currentCoreSubscription.isUpgradable;

  const getButtonText = () => {
    if (isGift) {
      return 'packagePicker.coreOffer.cta.gift';
    }
    if (isPackageDisabled) {
      return 'packagePicker.coreOffer.currentPlan';
    }
    return isCurrent
      ? 'packagePicker.coreOffer.cta.extend'
      : isRecommended
      ? 'packagePicker.coreOffer.cta.upgradeNow'
      : 'packagePicker.coreOffer.cta.upgrade';
  };

  const hasBrandedButton = isMobile || isRecommended;
  const showCurrentPlanTag = isCurrent && !isGift;

  return (
    <div
      className={cn(
        s['root'],
        isRecommended && s['recommended'],
        hasDiscount && s['with-discount']
      )}
    >
      {hasDiscount && (
        <div className={s['sale-badge']}>
          {t('packagePicker.coreOffer.saleBadge', {
            discountPercent: yearlyPackage.discount
              ? yearlyPackage.discountPercent
              : monthlyPackage.discountPercent,
          })}
        </div>
      )}
      {showCurrentPlanTag && (
        <div className={cn(s['tag'], s['current-plan'])}>
          {t('packagePicker.coreOffer.currentPlan')}
        </div>
      )}
      {isRecommended && !showCurrentPlanTag && (
        <div className={s['tag']}>{t('packagePicker.coreOffer.popular')}</div>
      )}
      <div className={s['core-logo']}>
        <UserSymbol
          userSymbol={monthlyPackage.coreType as any}
          className={s['core-symbol']}
        />
      </div>
      <div className={s['title']}>
        {t(packagesData[monthlyPackage.coreType].title)}
      </div>
      {!isMobile && <div className={s['divider']} />}
      <div className={s['price-wrapper']}>
        <div className={s['price']}>
          {formatDollars(
            getPriceForPackagePicker({
              monthlyPackage,
              yearlyPackage,
              showDiscount: sale?.ppShowDiscount,
            })
          )}
        </div>
        <div className={s['period']}>
          {t('packagePicker.coreOffer.perMonth')}
        </div>
      </div>
      {hasDiscount && (
        <div className={s['price-before']}>
          {formatDollars(
            getPriceBeforeDiscountForPackagePicker({
              monthlyPackage,
              yearlyPackage,
            })
          )}
        </div>
      )}
      {showCTA && hasBrandedButton && (
        <BrandedButton
          className={cn(s['button'], s['branded-button'])}
          size={'large'}
          variant={'core-orange'}
          onClick={() =>
            handleSelect([
              monthlyPackage.subproductId,
              yearlyPackage.subproductId,
            ])
          }
          disabled={isDisabled}
          biData={BiData<PackagePickerClickBiEvent>({
            evid: 501,
            button_type: biButtonType as any,
            core_type: biCoreType,
            give_core_or_become_core: alreadyHasSubscription
              ? 'upgrade_core'
              : isGift
              ? 'give_core'
              : 'become_core',
          })}
        >
          {t(getButtonText())}
        </BrandedButton>
      )}
      {showCTA && !hasBrandedButton && (
        <GhostButton
          className={s['button']}
          size={'large'}
          variant={'core-orange'}
          onClick={() =>
            handleSelect([
              monthlyPackage.subproductId,
              yearlyPackage.subproductId,
            ])
          }
          disabled={isDisabled}
          biData={BiData<PackagePickerClickBiEvent>({
            evid: 501,
            button_type: 'select',
            core_type: biCoreType,
            give_core_or_become_core: alreadyHasSubscription
              ? 'upgrade_core'
              : isGift
              ? 'give_core'
              : 'become_core',
          })}
        >
          {t(getButtonText())}
        </GhostButton>
      )}

      <BillingCyclePopup
        selectedPackage={selectedPackage}
        coreType={monthlyPackage.coreType as CoreTypes}
        isOpen={isPopupOpen}
        onRequestClose={() => setIsPopupOpen(false)}
        withCloseButton
      />
    </div>
  );
};
CorePrice.displayName = 'CorePrice';

export default CorePrice;
